// src/Components/Settings/ProfileHeaderCard.jsx
// Top-of-Settings profile + coin balance card, replacing Account.jsx's old
// standalone "Coin Balance Section". Mirrors mobile's ProfileCoinCard.
import React, { useState } from "react";
import { ChevronRight, Crown, Pencil, Plus, Smile } from "lucide-react";
import { MascotAvatar, useMyMascot } from "../Mascot";
import BottomSheet from "../BottomSheet";
import MascotEditor from "./MascotEditor";
import SettingsRow from "./SettingsRow";

const ProfileHeaderCard = ({
  accountInfo,
  displayName,
  onEditName,
  coinBalance,
  isPro,
  proSubtitle,
  onBuyCoins,
  onGoPro,
  onManagePro,
}) => {
  // A visitor has no account at all yet, so there is nothing to buy coins
  // for. Anonymous guests do have one and can still top up.
  const isVisitor = !!accountInfo.isVisitor;
  const isGuest = accountInfo.type === "Guest";
  const mascot = useMyMascot();
  const [showEditProfile, setShowEditProfile] = useState(false);
  const [showMascotEditor, setShowMascotEditor] = useState(false);
  // The name other players see, like mobile's profile card; the account
  // email is secondary.
  const title = displayName || accountInfo.displayName || accountInfo.email || "Guest";
  // Spelled out, not shortened: say exactly how this player is signed in.
  const status = isGuest
    ? "Playing as a guest"
    : [
        accountInfo.type === "Google Account" ? "Signed in with Google" : "Signed in with email",
        accountInfo.email,
      ].filter(Boolean).join(" · ");

  return (
    <div className="bg-brand-surface rounded-2xl border border-brand-border shadow-[0_1px_3px_rgba(0,0,0,0.04)] mb-5 overflow-hidden">
      {/* Who you are: the mascot and the name other players see, with
          clearly labelled buttons to change either. */}
      <div className="px-5 pt-5 pb-4 text-center">
        <div className="w-16 h-16 mx-auto rounded-full bg-brand-accentMuted flex items-end justify-center overflow-hidden" aria-hidden="true">
          <MascotAvatar mascot={mascot} size={mascot.base === "pencil" ? 30 : 44} shadow={false} />
        </div>
        <div className="mt-2 flex items-center justify-center gap-2 min-w-0">
          <p className="text-lg font-bold text-brand-text truncate">{title}</p>
          {isPro && (
            <span className="flex items-center gap-1 text-[10px] font-bold bg-yellow-400 text-yellow-900 px-2 py-0.5 rounded-full flex-shrink-0">
              <Crown className="w-2.5 h-2.5" aria-hidden="true" />
              Pro
            </span>
          )}
        </div>
        <p className="text-xs text-brand-textSecondary truncate">{status}</p>
        <button
          type="button"
          onClick={() => setShowEditProfile(true)}
          className="mt-3 inline-flex items-center justify-center gap-1.5 min-h-[44px] px-6 rounded-xl border border-red-200 text-sm font-semibold text-brand-accent hover:bg-red-50 transition-colors focus-visible:outline focus-visible:outline-2 focus-visible:outline-brand-accent"
        >
          <Pencil size={14} aria-hidden="true" />
          Edit profile
        </button>
      </div>

      {/* Two numbers side by side instead of a row each */}
      <div className="grid grid-cols-2 border-t border-brand-borderLight">
        <div className="px-4 py-3 border-r border-brand-borderLight flex items-center justify-between gap-2">
          <div className="min-w-0">
            <p className="text-[11px] font-semibold uppercase tracking-wide text-brand-textSecondary">Coins</p>
            <p className="text-lg font-bold text-brand-text leading-tight">{coinBalance.toLocaleString()}</p>
          </div>
          {!isVisitor && (
            <button
              onClick={onBuyCoins}
              aria-label="Get coins"
              className="w-9 h-9 flex items-center justify-center rounded-full bg-brand-accent hover:bg-red-700 text-white transition-colors flex-shrink-0"
            >
              <Plus size={16} aria-hidden="true" />
            </button>
          )}
        </div>
        <div className="px-4 py-3">
          <p className="text-[11px] font-semibold uppercase tracking-wide text-brand-textSecondary">Rooms created</p>
          <p className="text-lg font-bold text-brand-text leading-tight">{accountInfo.totalRooms}</p>
        </div>
      </div>

      {isPro ? (
        <button
          type="button"
          onClick={onManagePro}
          className="w-full px-5 py-3.5 min-h-[56px] border-t border-brand-borderLight hover:bg-brand-surface2 flex items-center gap-3 text-left transition-colors focus-visible:outline focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-brand-accent"
        >
          <Crown size={20} className="text-amber-500 flex-shrink-0" aria-hidden="true" />
          <span className="flex-1 min-w-0">
            <span className="block text-sm font-bold text-brand-text">
              NameIt! Pro <span className="text-amber-600">· Active</span>
            </span>
            {proSubtitle && <span className="block text-xs text-brand-textSecondary">{proSubtitle}</span>}
          </span>
          <ChevronRight size={16} className="text-brand-placeholder flex-shrink-0" aria-hidden="true" />
        </button>
      ) : (
        <button
          type="button"
          onClick={onGoPro}
          className="w-full px-5 py-3.5 min-h-[56px] border-t border-amber-100 bg-amber-50 hover:bg-amber-100 flex items-center gap-3 text-left transition-colors focus-visible:outline focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-brand-accent"
        >
          <Crown size={20} className="text-amber-500 flex-shrink-0" aria-hidden="true" />
          <span className="flex-1 min-w-0">
            <span className="block text-sm font-bold text-brand-text">Go Pro</span>
            <span className="block text-xs text-brand-textSecondary">10 players, unlimited rooms & more</span>
          </span>
          <ChevronRight size={16} className="text-amber-600 flex-shrink-0" aria-hidden="true" />
        </button>
      )}

      {/* Edit profile: a short list of what can be changed, each opening
          its own editor. */}
      <BottomSheet isOpen={showEditProfile} onClose={() => setShowEditProfile(false)} title="Edit profile">
        <div className="-mx-1">
          {onEditName && (
            <SettingsRow
              title="Name"
              subtitle="What other players see"
              value={title}
              icon={Pencil}
              onClick={() => { setShowEditProfile(false); onEditName(); }}
            />
          )}
          <SettingsRow
            title="Mascot"
            subtitle="Your Namey in games"
            icon={Smile}
            onClick={() => { setShowEditProfile(false); setShowMascotEditor(true); }}
            rightElement={
              <span className="flex items-center gap-2">
                <span className="flex h-10 w-10 items-end justify-center">
                  <MascotAvatar mascot={mascot} size={mascot.base === "pencil" ? 24 : 34} shadow={false} />
                </span>
                <ChevronRight size={16} className="text-brand-placeholder" />
              </span>
            }
          />
        </div>
      </BottomSheet>

      <BottomSheet isOpen={showMascotEditor} onClose={() => setShowMascotEditor(false)} title="Your Namey" subtitle="Pick a base, colour and face">
        <MascotEditor />
      </BottomSheet>
    </div>
  );
};

export default ProfileHeaderCard;
